import { followAccountSuccess } from 'mastodon/actions/accounts_typed';
import { apiRequest } from 'mastodon/api';
import type { ApiRelationshipJSON } from 'mastodon/api_types/relationships';
import type {
  ApiRoomJSON,
  ApiRoomChatMessageJSON,
  ApiRoomInviteJSON,
  ApiStreamEmoteJSON,
  LivekitTokenResponse,
} from 'mastodon/api_types/rooms';
import type {
  GiftSubCatalogue,
  GiftSubCheckout,
  GiftSubProvider,
} from 'mastodon/features/rooms/gift_subs';
import type { TopGiftersBoard } from 'mastodon/features/rooms/top_gifters';
import { me } from 'mastodon/initial_state';
import type { AppDispatch } from 'mastodon/store';

export const ROOMS_FETCH_REQUEST = 'ROOMS_FETCH_REQUEST';
export const ROOMS_FETCH_SUCCESS = 'ROOMS_FETCH_SUCCESS';
export const ROOMS_FETCH_FAIL = 'ROOMS_FETCH_FAIL';
export const ROOM_CREATE_SUCCESS = 'ROOM_CREATE_SUCCESS';
export const ROOM_JOIN_SUCCESS = 'ROOM_JOIN_SUCCESS';
export const ROOM_LEAVE_SUCCESS = 'ROOM_LEAVE_SUCCESS';
export const ROOM_END_SUCCESS = 'ROOM_END_SUCCESS';
export const ROOM_UPDATE = 'ROOM_UPDATE';
export const ROOM_CLEAR = 'ROOM_CLEAR';
export const ROOM_SET_PARTICIPANT_VOLUME = 'ROOM_SET_PARTICIPANT_VOLUME';
export const ROOM_CHAT_FETCH_SUCCESS = 'ROOM_CHAT_FETCH_SUCCESS';
export const ROOM_CHAT_SEND_SUCCESS = 'ROOM_CHAT_SEND_SUCCESS';
export const ROOM_CHAT_NEW_MESSAGES = 'ROOM_CHAT_NEW_MESSAGES';
export const ROOM_CHAT_MESSAGE_DELETED = 'ROOM_CHAT_MESSAGE_DELETED';
// Ticket 64 - Raid Stream. The room a viewer's poll has just discovered they
// are being carried to, and the viewer declining to be carried.
export const ROOM_RAID_PENDING = 'ROOM_RAID_PENDING';
export const ROOM_RAID_DISMISSED = 'ROOM_RAID_DISMISSED';

export const fetchRooms = (query?: string) => async (dispatch: AppDispatch) => {
  dispatch({ type: ROOMS_FETCH_REQUEST });
  try {
    const params: Record<string, string> = {};
    if (query) params.q = query;
    const data = await apiRequest<ApiRoomJSON[]>('GET', 'v1/rooms', { params });
    dispatch({ type: ROOMS_FETCH_SUCCESS, rooms: data });
  } catch (err) {
    dispatch({ type: ROOMS_FETCH_FAIL, error: err });
  }
};

export const createRoom =
  (
    title: string,
    description?: string,
    communityId?: string | null,
    kind?: 'audio' | 'video',
    visibility?: 'public' | 'private',
    // Ticket 111. false = start the room WITHOUT entering it. Making a room the
    // active room is what connects this browser to it, app-wide (the mini
    // player), so a host who streams from OBS and stays on the creator
    // dashboard must not have it set — or they watch their own stream back.
    enter = true,
  ) =>
  async (dispatch: AppDispatch) => {
    const body: Record<string, string> = { title };
    if (description) body.description = description;
    if (communityId) body.community_id = communityId;
    if (kind) body.kind = kind;
    if (visibility) body.visibility = visibility;
    const data = await apiRequest<ApiRoomJSON>('POST', 'v1/rooms', {
      data: body,
    });
    dispatch({ type: ROOM_CREATE_SUCCESS, room: data, enter });
    return data;
  };

// Flip a live room between public and private. Host and co-hosts only —
// the server enforces that; this just surfaces the result. ROOM_UPDATE so the
// change lands on the open room without waiting for the next poll.
export const updateRoomVisibility =
  (roomId: string, visibility: 'public' | 'private') =>
  async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>('PATCH', `v1/rooms/${roomId}`, {
      data: { visibility },
    });
    dispatch({ type: ROOM_UPDATE, room: data });
    return data;
  };

// Ticket 105. The server's caps, mirrored so a form can stop at them instead of
// letting the host type something that is refused on save.
export const ROOM_TITLE_MAX_LENGTH = 120;
export const ROOM_DESCRIPTION_MAX_LENGTH = 200;

// Change a live room's title and/or description (ticket 105). Host only — the
// server enforces that — and it rewrites the announcement post to match. An
// empty description clears it, which puts the stock line back in the post.
export const updateRoomDetails =
  (roomId: string, details: { title?: string; description?: string }) =>
  async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>('PATCH', `v1/rooms/${roomId}`, {
      data: details,
    });
    dispatch({ type: ROOM_UPDATE, room: data });
    return data;
  };

// `key` is a private room's join key from the link that was opened (ticket
// 104). Only the first join needs it: once inside, being a participant is what
// lets every other room request through.
export const joinRoom =
  (roomId: string, key?: string | null) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/join`,
      key ? { data: { key } } : {},
    );
    dispatch({ type: ROOM_JOIN_SUCCESS, room: data });
    return data;
  };

export const leaveRoom = (roomId: string) => async (dispatch: AppDispatch) => {
  await apiRequest('POST', `v1/rooms/${roomId}/leave`);
  dispatch({ type: ROOM_LEAVE_SUCCESS, roomId });
};

/**
 * The host deliberately leaving (ticket 69). Its own endpoint, not `leave`:
 * `leave` is what the page-unload beacon fires, and a refresh must not hand a
 * host's room away. The server hands the room to the longest-standing co-host,
 * or ends it when there is none, and says which in the response.
 */
export const hostLeaveRoom =
  (roomId: string) => async (dispatch: AppDispatch) => {
    await apiRequest('POST', `v1/rooms/${roomId}/host_leave`);
    dispatch({ type: ROOM_LEAVE_SUCCESS, roomId });
  };

// The host's presence heartbeat (ticket 69). Fire-and-forget: it feeds a flag
// that only matters an hour from now, so a dropped beat must never surface as
// an error to somebody sitting in a room.
export const sendHostActivity = (roomId: string) => async () => {
  try {
    await apiRequest('POST', `v1/rooms/${roomId}/host_activity`);
  } catch {
    // Deliberately silent.
  }
};

// Ticket 71. Follows the room's host and lets the server announce it.
//
// Deliberately NOT the ordinary follow endpoint followed by a "please announce"
// call: the server performs the follow itself so that an announcement cannot be
// minted for a follow that never happened. The relationship comes back in the
// standard shape and is dispatched through the ordinary follow-success action,
// so every follow button on the site (profile, hovercard) stays in step with
// this one for free.
//
// Resolves to whether this was the pair's first ever follow, which is the only
// thing allowed to raise the alert on the stream.
export const followRoomHost =
  (roomId: string) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    const data = await apiRequest<{
      first_follow: boolean;
      relationship: ApiRelationshipJSON;
    }>('POST', `v1/rooms/${roomId}/follow_host`);

    dispatch(
      followAccountSuccess({
        relationship: data.relationship,
        alreadyFollowing: false,
      }),
    );

    return data.first_follow;
  };

// Ticket 73. Both plain requests rather than thunks: gifting has no redux
// state -- the catalogue is read when the menu opens and the purchase leaves
// the site for Stripe, so there is nothing to keep in the store.
export const fetchGiftSubCatalogue = (roomId: string) =>
  apiRequest<GiftSubCatalogue>('GET', `v1/rooms/${roomId}/gift_subs`);

/**
 * Open a checkout for a gift package on the chosen point of sale.
 *
 * The package key is all the client sends. It never names an amount, and the
 * asset only narrows WHICH dollar-pegged coin the invoice is denominated in —
 * a client that could name its own price could buy 100 subs for a penny.
 *
 * `provider` is omitted rather than defaulted to 'stripe' on the wire so the
 * request stays byte-identical to the one this app sent before crypto existed.
 */
export const startGiftSubCheckout = (
  roomId: string,
  packageKey: string,
  options?: { provider?: GiftSubProvider; asset?: string },
) =>
  apiRequest<GiftSubCheckout>('POST', `v1/rooms/${roomId}/gift_subs`, {
    data: {
      package: packageKey,
      ...(options?.provider === 'privcart'
        ? { provider: 'privcart', asset: options.asset }
        : {}),
    },
  });

// Ticket 75. The top-gifters board above the chat. A plain request like the
// gift catalogue above and for the same reason: it is read where it is drawn
// and nothing else in the app wants it, so there is no redux state to keep.
export const fetchTopGifters = (roomId: string) =>
  apiRequest<TopGiftersBoard>('GET', `v1/rooms/${roomId}/top_gifters`);

export const endRoom = (roomId: string) => async (dispatch: AppDispatch) => {
  const data = await apiRequest<ApiRoomJSON>(
    'POST',
    `v1/rooms/${roomId}/end_room`,
  );
  dispatch({ type: ROOM_END_SUCCESS, room: data });
};

export const fetchRoom = (roomId: string) => async (dispatch: AppDispatch) => {
  const data = await apiRequest<ApiRoomJSON>('GET', `v1/rooms/${roomId}`);
  // If we are no longer in the room (e.g., kicked by host), clear active room
  const stillIn = data.participants.some((p) => p.account.id === me!);
  if (!stillIn) {
    dispatch({ type: ROOM_LEAVE_SUCCESS, roomId });

    // Ticket 64 - Raid Stream. THIS is the whole redirect mechanism. Rooms have
    // no socket layer, so the only channel that reaches a viewer is the 3s poll
    // they were already running, and the only moment they can be redirected is
    // the one where that poll finds them no longer in the room.
    //
    // That timing is also exactly the rule the ticket asked for: "only viewers
    // who already joined and are still in the channel". A browser that stopped
    // polling never runs this line, so nobody who has left is chased.
    //
    // The HOST is deliberately excluded. They are the one raiding; their
    // audience goes, they do not get dropped into somebody else's room.
    if (data.raided_to_room_id && data.host_account_id !== me) {
      dispatch({
        type: ROOM_RAID_PENDING,
        raid: {
          targetRoomId: data.raided_to_room_id,
          targetTitle: data.raided_to_room_title ?? null,
          fromTitle: data.title,
        },
      });
    }

    return data;
  }
  dispatch({ type: ROOM_UPDATE, room: data });
  return data;
};

/**
 * Ticket 64. The live rooms this host may raid.
 *
 * Deliberately the ORDINARY rooms endpoint with a filter, not a raid-specific
 * one. Two live-room lists would eventually disagree about which rooms are
 * live, and the disagreement would surface as a host picking a room the raid
 * then refuses.
 */
export const fetchRaidTargets = async (query?: string) => {
  const params: Record<string, string> = { raid_target: '1' };
  if (query) params.q = query;

  return apiRequest<ApiRoomJSON[]>('GET', 'v1/rooms', { params });
};

/**
 * Ticket 64. End this room and hand whoever is still in it to `targetRoomId`.
 *
 * Dispatches ROOM_END_SUCCESS because from the raiding host's point of view
 * that is what happened: their room is over. Errors are rethrown rather than
 * swallowed - a raid that failed left the room LIVE, and a host who is told
 * nothing will assume their broadcast has closed and walk away from it.
 */
export const raidRoom =
  (roomId: string, targetRoomId: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/raid`,
      { data: { target_room_id: targetRoomId } },
    );

    dispatch({ type: ROOM_END_SUCCESS, room: data });
    return data;
  };

export const promoteParticipant =
  (roomId: string, accountId: string, role: string) =>
  async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/promote`,
      {
        data: { account_id: accountId, role },
      },
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

export const forceMuteParticipant =
  (roomId: string, accountId: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/mute_participant`,
      {
        data: { account_id: accountId },
      },
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

export const unmuteParticipant =
  (roomId: string, accountId: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/unmute_participant`,
      {
        data: { account_id: accountId },
      },
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

export const removeParticipant =
  (roomId: string, accountId: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/remove_participant`,
      {
        data: { account_id: accountId },
      },
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

export const muteAll = (roomId: string) => async (dispatch: AppDispatch) => {
  const data = await apiRequest<ApiRoomJSON>(
    'POST',
    `v1/rooms/${roomId}/mute_all`,
  );
  dispatch({ type: ROOM_UPDATE, room: data });
};

export const unmuteAll = (roomId: string) => async (dispatch: AppDispatch) => {
  const data = await apiRequest<ApiRoomJSON>(
    'POST',
    `v1/rooms/${roomId}/unmute_all`,
  );
  dispatch({ type: ROOM_UPDATE, room: data });
};

export const raiseHand = (roomId: string) => async (dispatch: AppDispatch) => {
  const data = await apiRequest<ApiRoomJSON>(
    'POST',
    `v1/rooms/${roomId}/raise_hand`,
  );
  dispatch({ type: ROOM_UPDATE, room: data });
};

export const lowerHand =
  (roomId: string, accountId?: string) => async (dispatch: AppDispatch) => {
    const body: Record<string, string> = {};
    if (accountId) body.account_id = accountId;
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/lower_hand`,
      {
        data: body,
      },
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

export const saveRecording =
  (roomId: string, mediaAttachmentId: string) =>
  async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/save_recording`,
      {
        data: { media_attachment_id: mediaAttachmentId },
      },
    );
    dispatch({ type: ROOM_UPDATE, room: data });
    return data;
  };

export const uploadRoomRecording = (blob: Blob) => async () => {
  const formData = new FormData();
  formData.append('file', blob, 'room-recording.webm');
  // Use v1/media (synchronous) so the file is fully processed before we
  // try to attach it to the announcement status via UpdateStatusService.
  // v2/media returns 202 and processes async, which would fail the
  // "not_processed?" check in UpdateStatusService.
  const resp = await apiRequest<{ id: string }>('POST', 'v1/media', {
    data: formData,
  });
  return resp.id;
};

export interface RoomClipResponse {
  id: string;
  title: string;
  state: string;
  source_kind: string;
  duration_seconds: number;
  status_id: string | null;
  status_path: string | null;
  room_path: string | null;
  source_title: string | null;
}

/**
 * Upload a viewer's clip (ticket 56). Same deliberate v1/media choice as the
 * room recording above: the file must be fully processed before the clip
 * endpoint reads its duration out of the metadata, and v2 returns 202 with
 * nothing decoded yet.
 */
export const uploadRoomClip =
  (blob: Blob, mimeType: string) => async (): Promise<string> => {
    const formData = new FormData();
    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    formData.append('file', blob, `room-clip.${extension}`);
    const resp = await apiRequest<{ id: string }>('POST', 'v1/media', {
      data: formData,
    });
    return resp.id;
  };

export const createRoomClip =
  (roomId: string, mediaId: string, title: string) =>
  async (): Promise<RoomClipResponse> => {
    return apiRequest<RoomClipResponse>('POST', `v1/rooms/${roomId}/clips`, {
      data: { media_id: mediaId, title },
    });
  };

export const setParticipantVolume = (accountId: string, volume: number) => ({
  type: ROOM_SET_PARTICIPANT_VOLUME,
  accountId,
  volume,
});

export const fetchLivekitToken = (roomId: string) => async () => {
  return apiRequest<LivekitTokenResponse>(
    'POST',
    `v1/rooms/${roomId}/livekit_token`,
  );
};

export const fetchRoomChat =
  (roomId: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomChatMessageJSON[]>(
      'GET',
      `v1/rooms/${roomId}/chat_messages`,
    );
    dispatch({ type: ROOM_CHAT_FETCH_SUCCESS, chatMessages: data });
  };

export const sendRoomChatMessage =
  (
    roomId: string,
    content: string,
    mediaAttachmentId?: string,
    replyToId?: string,
  ) =>
  async (dispatch: AppDispatch) => {
    const body: Record<string, string> = { content };
    if (mediaAttachmentId) body.media_attachment_id = mediaAttachmentId;
    if (replyToId) body.reply_to_id = replyToId;
    const data = await apiRequest<ApiRoomChatMessageJSON>(
      'POST',
      `v1/rooms/${roomId}/chat_messages`,
      {
        data: body,
      },
    );
    dispatch({ type: ROOM_CHAT_SEND_SUCCESS, chatMessage: data });
    return data;
  };

export const pollRoomChat =
  (roomId: string, sinceId?: string) => async (dispatch: AppDispatch) => {
    // include_deleted: the server responds with short-lived tombstones for
    // moderator-deleted messages so we can drop them from the local list.
    const params: Record<string, string> = { include_deleted: 'true' };
    if (sinceId) params.since_id = sinceId;
    const data = await apiRequest<ApiRoomChatMessageJSON[]>(
      'GET',
      `v1/rooms/${roomId}/chat_messages`,
      { params },
    );
    if (data.length > 0) {
      dispatch({ type: ROOM_CHAT_NEW_MESSAGES, chatMessages: data });
    }
    return data;
  };

// The author deleting their own message, or the host deleting anyone's.
export const deleteRoomChatMessage =
  (roomId: string, messageId: string) => async (dispatch: AppDispatch) => {
    await apiRequest<ApiRoomChatMessageJSON>(
      'DELETE',
      `v1/rooms/${roomId}/chat_messages/${messageId}`,
    );
    dispatch({ type: ROOM_CHAT_MESSAGE_DELETED, messageId });
  };

export const pinRoomChatMessage =
  (roomId: string, messageId: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/chat_messages/${messageId}/pin`,
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

export const unpinRoomChatMessage =
  (roomId: string, messageId: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/chat_messages/${messageId}/unpin`,
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

// Jumbotron (host-only). The server responds with the re-rendered room, so
// ROOM_UPDATE carries the new pinned_items to this client immediately; other
// participants converge via the 3s room poll.
export const createRoomPinnedItem =
  (
    roomId: string,
    payload: {
      kind: 'link' | 'image';
      description: string;
      url?: string;
      image?: File;
      // An image pasted as a URL instead of uploaded; the server downloads it
      // out of band, so the response comes back with preview_state 'pending'.
      imageUrl?: string;
    },
  ) =>
  async (dispatch: AppDispatch) => {
    const form = new FormData();
    form.append('kind', payload.kind);
    form.append('description', payload.description);
    if (payload.url) form.append('url', payload.url);
    if (payload.image) form.append('image', payload.image);
    if (payload.imageUrl) form.append('image_url', payload.imageUrl);
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/pinned_items`,
      { data: form },
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

export const deleteRoomPinnedItem =
  (roomId: string, itemId: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'DELETE',
      `v1/rooms/${roomId}/pinned_items/${itemId}`,
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

// Jumbotron polls. Every one of these responds with the re-rendered room, so
// the acting client sees the new tally at once and everyone else converges on
// the 3s room poll — that is the whole "live results" mechanism, with no extra
// connection.
export const createRoomPoll =
  (roomId: string, question: string, options: string[]) =>
  async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/polls`,
      { data: { question, options } },
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

export const voteInRoomPoll =
  (roomId: string, pollId: string, optionIndex: number) =>
  async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/polls/${pollId}/vote`,
      { data: { option_index: optionIndex } },
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

export const closeRoomPoll =
  (roomId: string, pollId: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'POST',
      `v1/rooms/${roomId}/polls/${pollId}/close`,
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

export const clearRoomPoll =
  (roomId: string, pollId: string) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'DELETE',
      `v1/rooms/${roomId}/polls/${pollId}`,
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

// Host-only: seconds between messages per non-host account, 0 = off.
export const setRoomSlowMode =
  (roomId: string, seconds: number) => async (dispatch: AppDispatch) => {
    const data = await apiRequest<ApiRoomJSON>(
      'PUT',
      `v1/rooms/${roomId}/chat_settings`,
      { data: { slow_mode_seconds: seconds } },
    );
    dispatch({ type: ROOM_UPDATE, room: data });
  };

export interface RoomStreamKeyResponse {
  whip_url: string;
  stream_key: string;
  ingress_id: string;
  // Absent on older servers (forward-compat).
  expires_at?: string;
}

// The host's custom emote set for a room — fetched once on join, used for
// rendering and the chat emote picker.
export const fetchRoomEmotes = (roomId: string) => async () => {
  return apiRequest<ApiStreamEmoteJSON[]>('GET', `v1/rooms/${roomId}/emotes`);
};

// Homepage "Happening Now": up to 3 live rooms/streams ranked by personal
// relevance (server-side formula; see Rooms::SuggestionsController).
export const fetchRoomSuggestions = () => async () => {
  return apiRequest<ApiRoomJSON[]>('GET', 'v1/rooms/suggestions');
};

// Host-only, video rooms: provision/reuse the account's persistent WHIP
// ingress for OBS and return its connection settings. rotate=true burns the
// old key and mints a fresh one ("Reset key").
export const fetchRoomStreamKey =
  (roomId: string, rotate = false) =>
  async () => {
    return apiRequest<RoomStreamKeyResponse>(
      'POST',
      `v1/rooms/${roomId}/stream_key`,
      rotate ? { params: { rotate: true } } : undefined,
    );
  };

// Host-only, video rooms: start/stop the server-side VOD recording. State
// propagates to everyone through the polled room payload (`recording`).
export const startRoomRecording = (roomId: string) => async () => {
  return apiRequest<{ egress_id: string; recording_started_at: string }>(
    'POST',
    `v1/rooms/${roomId}/recording`,
  );
};

export const stopRoomRecording = (roomId: string) => async () => {
  return apiRequest<{ egress_id: string; finalizing: boolean }>(
    'DELETE',
    `v1/rooms/${roomId}/recording`,
  );
};

// Host-only: block an account from chatting in this room for a duration.
export const timeoutRoomChatUser =
  (roomId: string, accountId: string, durationSeconds: number) => async () => {
    return apiRequest<{ account_id: string; expires_at: string }>(
      'POST',
      `v1/rooms/${roomId}/chat_timeouts`,
      { data: { account_id: accountId, duration_seconds: durationSeconds } },
    );
  };

export const shipClientLogs =
  (roomId: string, entries: string[]) => async () => {
    try {
      await apiRequest('POST', `v1/rooms/${roomId}/client_log`, {
        data: { entries },
      });
      return true;
    } catch {
      // Never let log shipping break anything — but tell the caller, so
      // buffered entries can be re-queued (a failed ship usually means an
      // outage, the very evidence the logs exist to capture).
      return false;
    }
  };

// Invite someone you follow into a room you host or co-host. The server is the
// authority on both of those rules — this is just the call.
export const inviteToRoom = async (roomId: string, accountId: string) =>
  apiRequest<ApiRoomInviteJSON>('POST', `v1/rooms/${roomId}/invites`, {
    data: { account_id: accountId },
  });

export const fetchRoomInvites = async (roomId: string) =>
  apiRequest<ApiRoomInviteJSON[]>('GET', `v1/rooms/${roomId}/invites`);
